import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowDown,
  ArrowUpRight,
  Coffee as CoffeeIcon,
  Compass,
  ExternalLink,
  MapPin,
  Music2,
  Phone,
  Play,
  Utensils,
  Waves,
  Check,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/* ============================================================
   EDITABLE CONTENT
   ============================================================ */

const HOST = {
  name: 'Your Island Connection',
  websiteUrl: 'https://www.mainerentals.com',
  localGuideUrl: 'https://www.mainerentals.com/pages/local-area-guide/',
  tideChartUrl: 'https://www.mainerentals.com/pages/tide-chart/',
  phone: '(207) 833-7779',
  phoneRaw: '2078337779',
  logo: '/your-island-connection-logo-white_(1).svg',
};

const BOND = {
  name: 'Bond Coffee Roasters',
  websiteUrl: 'https://www.thebondcoffee.com',
};

const COFFEE = {
  name: 'Cottage Current',
  tagline: 'Cozy coast. Slow mornings.',
  size: '10 oz bag',
  price: '$27',
  paymentLink: 'https://buy.stripe.com/aFa00k5Px811gAtabU3VC0y',
};

const PLAYLIST_LINK = 'https://open.spotify.com/playlist/43LLtej0TsUKhA7Ym2RovW?si=jVxW4KqsRJWyqBQ7vgQVWw&utm_source=copy-link&pi=SHIJnzW3TgCaf';

const TRACKS = [
  { num: '01', title: 'Our Song', artist: 'Joe Henry', note: 'A songwriter who calls Harpswell home.' },
  { num: '02', title: 'Lost', artist: 'Matt Newberg', note: 'Raised in Harpswell. Music shaped by Maine roads, pines and rocky coastline.' },
  { num: '03', title: 'Best Laid Plans', artist: 'Dooryarders', note: 'Recorded in Harpswell. A little grit for the morning.' },
  { num: '04', title: 'Sail, Baby, Sail', artist: 'Lisa Redfern', note: 'Maine folk from a singer-songwriter with deep ties to the Harpswell community.' },
  { num: '05', title: 'Bay of Fundy', artist: 'Gordon Bok', note: 'One of Maine’s great maritime folk voices. Finish the coffee. Watch the water.' },
];

const LOCAL_MOVES = [
  {
    num: '01',
    category: 'EAT',
    destination: "Cook’s Lobster & Ale House",
    location: "Orr’s Island",
    copy: 'You came to coastal Maine. This seems like a fairly obvious place to start.',
    cta: 'FIND IT',
    url: 'https://www.mainerentals.com/pages/local-area-guide/',
    img: 'https://images.pexels.com/photos/15964419/pexels-photo-15964419.jpeg?auto=compress&cs=tinysrgb&w=900',
  },
  {
    num: '02',
    category: 'EXPLORE',
    destination: 'Johnson Field Preserve at Mackerel Cove',
    location: 'Bailey Island',
    copy: 'One of those places where Maine looks suspiciously like the version of Maine you pictured before getting here.',
    cta: 'GO EXPLORE',
    url: 'https://www.mainerentals.com/pages/local-area-guide/',
    img: 'https://images.pexels.com/photos/34993330/pexels-photo-34993330.jpeg?auto=compress&cs=tinysrgb&w=900',
  },
  {
    num: '03',
    category: 'GET OUT THERE',
    destination: 'Kayak Harpswell',
    location: 'Harpswell Islands',
    copy: 'Harpswell makes considerably more sense from the water.',
    cta: 'FIND A LAUNCH',
    url: 'https://www.mainerentals.com/pages/local-area-guide/',
    img: 'https://images.pexels.com/photos/14111368/pexels-photo-14111368.jpeg?auto=compress&cs=tinysrgb&w=900',
  },
  {
    num: '04',
    category: 'WANDER',
    destination: 'The Working Waterfront',
    location: 'Harpswell',
    copy: 'This isn’t a manufactured fishing village. People still make their living out there. Take the long way around.',
    cta: 'GO WANDER',
    url: 'https://www.mainerentals.com/pages/local-area-guide/',
    img: 'https://images.pexels.com/photos/17492210/pexels-photo-17492210.jpeg?auto=compress&cs=tinysrgb&w=900',
  },
  {
    num: '05',
    category: 'TREAT',
    destination: "Orr’s Island Candy",
    location: "Orr’s Island",
    copy: 'Vacation has rules. This is apparently one of them.',
    cta: 'I ACCEPT',
    url: 'https://www.mainerentals.com/pages/local-area-guide/',
    img: 'https://images.pexels.com/photos/8925533/pexels-photo-8925533.jpeg?auto=compress&cs=tinysrgb&w=900',
  },
];

const HERO_IMG = '/bailey-island-western-shore.jpg';
const MORNING_IMG = 'https://images.pexels.com/photos/17491959/pexels-photo-17491959.jpeg?auto=compress&cs=tinysrgb&w=1600';
const SOUNDTRACK_IMG = 'https://images.pexels.com/photos/17492210/pexels-photo-17492210.jpeg?auto=compress&cs=tinysrgb&w=1600';
const HARPSWELL_IMG = 'https://images.pexels.com/photos/32820518/pexels-photo-32820518.jpeg?auto=compress&cs=tinysrgb&w=1600';
const FINAL_IMG = 'https://images.pexels.com/photos/97071/nature-landscape-ocean-beach-97071.jpeg?auto=compress&cs=tinysrgb&w=1600';

/* ============================================================
   END EDITABLE CONTENT
   ============================================================ */

export default function YourIslandConnection() {
  const [missionDone, setMissionDone] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    document.title = 'Your Island Connection · Good Morning, Harpswell';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'A morning coffee companion for guests of Your Island Connection in Harpswell, Maine. Coffee by Bond Coffee Roasters, local picks, and the Harpswell soundtrack.');

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('amelia-in');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    document.querySelectorAll('.amelia-reveal').forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0f1f33] text-[#f4ece0] antialiased">
      {/* ═══ HERO ═══ */}
      <section className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Foggy coastal Maine morning"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0f1f33]/20 via-transparent to-[#0f1f33]/40" />
        <div className="absolute inset-x-0 bottom-0 z-[1] h-2/3 bg-gradient-to-t from-[#0f1f33] via-[#0f1f33]/60 to-transparent" />
        {/* Fog layers */}
        <div className="fog-layer absolute -left-1/4 top-1/4 z-[2] h-40 w-[150%] bg-gradient-to-r from-transparent via-white/8 to-transparent blur-2xl" />
        <div className="fog-layer absolute right-0 top-1/2 z-[2] h-32 w-[120%] bg-gradient-to-l from-transparent via-white/6 to-transparent blur-2xl" style={{ animationDelay: '4s' }} />

        <div className="relative z-10 mx-auto w-full max-w-2xl px-6 pb-20 pt-32">
          <div className="max-w-xl rounded-3xl border border-white/15 bg-[#0f1f33]/55 p-6 shadow-2xl backdrop-blur-[3px] sm:p-8">
          <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.3em] text-[#ffd0a3]">
            Your Island Connection <span className="text-white/40">×</span> Bond Coffee
          </p>
          <h1 className="font-serif text-5xl leading-[0.9] tracking-tight text-white sm:text-6xl">
            Good morning,<br />Harpswell.
          </h1>
          <div className="mt-7">
            <p className="font-serif text-3xl text-[#fda36b]">{COFFEE.name}</p>
            <p className="mt-1 text-sm uppercase tracking-[0.2em] text-white/60">{COFFEE.tagline}</p>
          </div>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/75">
            You’re on island time now. We made this coffee especially for guests of Your Island Connection — something local to start the morning before you figure out where the tide, trails and lobster rolls take you.
          </p>
          <button
            onClick={() => scrollTo('coffee')}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
          >
            Start your morning <ArrowDown className="h-4 w-4" />
          </button>
          </div>
        </div>
      </section>

      {/* ═══ FIRST THINGS FIRST ═══ */}
      <section id="coffee" className="scroll-mt-20 bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-2xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">First things first</p>
            <h2 className="mt-4 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">
              First things first.<br />Coffee.
            </h2>
          </div>
          <div className="amelia-reveal mt-8 space-y-4 text-[15px] leading-relaxed text-white/70">
            <p>This little bag was roasted in small batches by Bond Coffee Roasters for mornings exactly like this one.</p>
            <p>Coffee brewing. Water outside. Maybe some fog. And absolutely nowhere you need to be for the next five minutes.</p>
          </div>
          <div className="amelia-reveal mt-10 rounded-2xl border border-[#fda36b]/25 bg-[#fda36b]/8 p-8 text-center">
            <p className="font-serif text-4xl text-[#fda36b]">{COFFEE.name}</p>
            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-white/60">{COFFEE.tagline}</p>
            <p className="mt-5 text-xs uppercase tracking-[0.14em] text-white/40">Roasted exclusively for guests of Your Island Connection</p>
          </div>
        </div>
      </section>

      {/* ═══ BREW IT ═══ */}
      <section id="brew" className="scroll-mt-20 bg-[#132947] py-24">
        <div className="mx-auto max-w-2xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">Brew it</p>
            <h2 className="mt-4 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">Make the coffee</h2>
            <p className="mt-3 text-lg text-white/50">You’ve got Maine to get to.</p>
          </div>

          <div className="amelia-reveal mt-10 space-y-5">
            {/* Drip */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
              <div className="flex items-center gap-3">
                <CoffeeIcon className="h-5 w-5 text-[#fda36b]" />
                <h3 className="font-serif text-xl text-white">Standard Drip Machine</h3>
              </div>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-white/70">
                <p>If using the full 3 oz guest bag: use the whole bag for approximately one full standard pot.</p>
                <p>Add water. Press the button. Go look out the window.</p>
                <p className="text-white/50">For a smaller pot, start with roughly half the bag.</p>
              </div>
              <p className="mt-4 text-xs italic text-white/35">Every cottage coffee maker seems to have its own personality. Adjust accordingly.</p>
            </div>

            {/* K-Cup */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
              <div className="flex items-center gap-3">
                <CoffeeIcon className="h-5 w-5 text-[#fda36b]" />
                <h3 className="font-serif text-xl text-white">Reusable K-Cup</h3>
              </div>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-white/70">
                <p>If the house has a Keurig and reusable pod, start with approximately:</p>
                <p className="font-serif text-2xl text-[#fda36b]">2 tablespoons per cup</p>
                <p>Adjust to taste.</p>
              </div>
            </div>
          </div>

          <p className="amelia-reveal mt-8 text-center font-serif text-xl italic text-white/40">There is no coffee police.</p>
        </div>
      </section>

      {/* ═══ MORNING MISSION ═══ */}
      <section className="relative overflow-hidden">
        <img
          src={MORNING_IMG}
          alt="Foggy morning at a Maine harbor"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0f1f33]/70 via-[#0f1f33]/50 to-[#0f1f33]" />

        <div className="mx-auto max-w-2xl px-6 py-28 text-center">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">Before you do anything else…</p>
            <h2 className="mt-6 font-serif text-6xl leading-[0.9] text-white sm:text-7xl">Go outside.</h2>
            <div className="mt-8 space-y-3 text-[15px] leading-relaxed text-white/75">
              <p>Seriously.</p>
              <p>Bring the coffee.</p>
              <p>Porch. Dock. Rocks. Yard. Wherever the water is.</p>
              <p>Stand there for five minutes without doing anything productive.</p>
              <p className="font-serif text-lg italic text-[#fda36b]">That’s the assignment.</p>
            </div>
            <div className="mt-10">
              {!missionDone ? (
                <button
                  onClick={() => setMissionDone(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
                >
                  Assignment accepted
                </button>
              ) : (
                <div className="check-pop inline-flex items-center gap-3 rounded-full border-2 border-[#fda36b]/50 bg-[#fda36b]/10 px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#fda36b]">
                  <Check className="h-5 w-5" />
                  Morning mission complete
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HARPSWELL SOUNDTRACK ═══ */}
      <section className="relative overflow-hidden bg-[#132947]">
        <img
          src={SOUNDTRACK_IMG}
          alt="Foggy harbor with boats at rest"
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-12"
          loading="lazy"
        />
        <div className="absolute inset-0 -z-0 bg-gradient-to-b from-[#132947]/85 via-[#132947]/90 to-[#132947]" />
        {/* Subtle fog drift */}
        <div className="fog-layer absolute -left-1/4 top-1/3 -z-0 h-32 w-[150%] bg-gradient-to-r from-transparent via-white/4 to-transparent blur-2xl" />

        <div className="relative mx-auto max-w-2xl px-6 py-24">
          <div className="amelia-reveal text-center">
            <div className="mx-auto mb-5 flex items-center justify-center gap-2 text-[#fda36b]">
              <span className="h-px w-8 bg-[#fda36b]/30" />
              <Music2 className="h-4 w-4" />
              <span className="h-px w-8 bg-[#fda36b]/30" />
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">The soundtrack</p>
            <h2 className="mt-4 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">Sounds like Harpswell</h2>
            <p className="mt-4 text-lg text-white/55">Five songs. All with a reason to be here.</p>
            <p className="mt-4 max-w-md mx-auto text-sm leading-relaxed text-white/50">
              We could have made you a generic coastal playlist. But you’re in Harpswell. So we went a little closer to home — musicians who live here, grew up here, make music here, and songs that sound like they belong near cold water and working harbors.
            </p>
          </div>

          {/* Track cards — record-sleeve styling */}
          <div className="mt-12 space-y-px overflow-hidden rounded-2xl border border-white/10 bg-[#0f1f33]/50">
            {TRACKS.map((track, i) => (
              <div
                key={track.num}
                className="amelia-reveal group flex items-center gap-4 px-6 py-5 transition hover:bg-[#fda36b]/5"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="font-serif text-4xl leading-none text-[#fda36b]/20 tabular-nums transition group-hover:text-[#fda36b]/40">
                  {track.num}
                </span>
                <div className="h-10 w-px bg-white/8" />
                <div className="flex-1">
                  <p className="font-serif text-lg leading-tight text-white">{track.title}</p>
                  <p className="text-sm text-white/55">{track.artist}</p>
                  <p className="mt-1 text-[13px] italic leading-relaxed text-white/35">{track.note}</p>
                </div>
                {/* Tiny waveform decorative element */}
                <div className="flex shrink-0 items-end gap-0.5 opacity-20 transition group-hover:opacity-40">
                  <span className="h-2 w-px bg-[#fda36b]" />
                  <span className="h-4 w-px bg-[#fda36b]" />
                  <span className="h-3 w-px bg-[#fda36b]" />
                  <span className="h-5 w-px bg-[#fda36b]" />
                  <span className="h-2 w-px bg-[#fda36b]" />
                </div>
              </div>
            ))}
          </div>

          <div className="amelia-reveal mt-10 text-center">
            <a
              href={PLAYLIST_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
            >
              <Play className="h-4 w-4 fill-current" /> Play the Harpswell soundtrack
            </a>
            <p className="mt-4 text-xs italic text-white/35">Locally sourced. Like the coffee. Sort of.</p>
          </div>
        </div>
      </section>

      {/* ═══ YOU'RE IN HARPSWELL ═══ */}
      <section className="relative overflow-hidden">
        <img
          src={HARPSWELL_IMG}
          alt="Autumn coastal Maine shoreline"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0f1f33]/80 via-[#0f1f33]/40 to-[#0f1f33]" />

        <div className="mx-auto max-w-2xl px-6 py-28">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">You’re in Harpswell</p>
            <h2 className="mt-4 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">
              You’re not just in Maine.<br />You’re in Harpswell.
            </h2>
            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-white/70">
              <p>Harpswell winds through Casco Bay in a collection of islands, coves, working harbors and fishing villages.</p>
              <p>Your Island Connection has been helping people find their way around this corner of Maine since 1982. They know the islands.</p>
              <p>Rather than give you a giant tourist checklist, here are a few things we’d do.</p>
            </div>
            <button
              onClick={() => scrollTo('moves')}
              className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#fda36b] hover:text-[#fda36b]"
            >
              Show me the good stuff <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FIVE LOCAL MOVES ═══ */}
      <section id="moves" className="scroll-mt-20 bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-2xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">5 things we’d do</p>
            <h2 className="mt-4 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">5 things we’d do</h2>
            <p className="mt-3 text-lg text-white/50">If we woke up here this morning.</p>
          </div>

          <div className="mt-12 space-y-6">
            {LOCAL_MOVES.map((move) => (
              <article key={move.num} className="amelia-reveal overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                <div className="relative h-52 overflow-hidden">
                  <img src={move.img} alt={move.destination} className="h-full w-full object-cover transition duration-700 hover:scale-105" loading="lazy" />
                  <span className="absolute left-4 top-4 rounded-full bg-[#132947]/80 px-3 py-1 text-[10px] font-bold tracking-[0.18em] text-[#fda36b] backdrop-blur-sm">
                    {move.category}
                  </span>
                </div>
                <div className="p-6">
                  <p className="font-serif text-2xl text-white">{move.destination}</p>
                  <p className="mt-1 text-sm text-[#fda36b]/80">{move.location}</p>
                  <p className="mt-3 text-sm leading-relaxed text-white/65">{move.copy}</p>
                  <a
                    href={move.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#fda36b] transition hover:text-white"
                  >
                    {move.cta} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="amelia-reveal mt-12 text-center">
            <p className="text-lg text-white/60">Want more?</p>
            <p className="mt-1 text-sm text-white/40">Your Island Connection actually knows this place.</p>
            <a
              href={HOST.localGuideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
            >
              Open the full local guide <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ═══ THE TIDE ═══ */}
      <section className="relative overflow-hidden bg-[#132947] py-24">
        {/* Wave lines */}
        <div className="absolute inset-0 -z-10 opacity-20">
          <div className="wave-anim absolute top-1/2 left-0 h-px w-[200%] bg-gradient-to-r from-transparent via-[#fda36b]/30 to-transparent" />
          <div className="wave-anim absolute top-[55%] left-0 h-px w-[200%] bg-gradient-to-r from-transparent via-[#fda36b]/20 to-transparent" style={{ animationDelay: '3s' }} />
          <div className="wave-anim absolute top-[60%] left-0 h-px w-[200%] bg-gradient-to-r from-transparent via-[#fda36b]/15 to-transparent" style={{ animationDelay: '6s' }} />
        </div>

        <div className="mx-auto max-w-2xl px-6 text-center">
          <div className="amelia-reveal">
            <Waves className="mx-auto h-8 w-8 text-[#fda36b]" />
            <h2 className="mt-6 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">The ocean has a schedule.</h2>
            <p className="mt-4 font-serif text-2xl text-[#fda36b]">You don’t have to.</p>
            <p className="mt-6 max-w-md mx-auto text-[15px] leading-relaxed text-white/70">
              But if you’re exploring coves, beaches, docks or launching a kayak, knowing what the tide is doing around here isn’t a terrible idea.
            </p>
            <a
              href={HOST.tideChartUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#fda36b]/40 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#fda36b] transition hover:bg-[#fda36b]/10 hover:border-[#fda36b]"
            >
              Check today’s tides <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ═══ TAKE THE ISLAND HOME ═══ */}
      <section className="bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-2xl px-6">
          <div className="amelia-reveal rounded-2xl border border-[#fda36b]/25 bg-[#132947] p-8 text-center sm:p-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">Like the coffee?</p>
            <h2 className="mt-4 font-serif text-4xl leading-[0.95] text-white sm:text-5xl">Take the island home.</h2>
            <p className="mt-6 max-w-md mx-auto text-[15px] leading-relaxed text-white/70">
              If Cottage Current became part of your Maine morning, we’ll send you more. A full-size bag, freshly roasted by Bond Coffee and delivered right to your door.
            </p>

            <div className="mt-8 rounded-xl border border-white/10 bg-[#0f1f33]/60 p-6">
              <p className="font-serif text-3xl text-white">{COFFEE.name}</p>
              <p className="mt-1 text-sm uppercase tracking-[0.14em] text-white/50">{COFFEE.size}</p>
              <p className="mt-4 font-serif text-5xl text-[#fda36b]">{COFFEE.price}<span className="text-lg text-white/50"> shipped</span></p>
              <p className="mt-3 text-xs text-white/40">Freshly roasted. Packed by Bond. Delivered to your door.</p>
              <a
                href={COFFEE.paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#fda36b] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
              >
                Send me a bag <ArrowUpRight className="h-4 w-4" />
              </a>
              <p className="mt-4 text-xs italic text-white/35">Cheaper than buying a cottage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ YOUR HOSTS ═══ */}
      <section className="bg-[#132947] py-24">
        <div className="mx-auto max-w-2xl px-6">
          <div className="amelia-reveal">
            <img src={HOST.logo} alt="Your Island Connection" className="h-16 w-auto opacity-90" />
            <h2 className="mt-6 font-serif text-3xl leading-tight text-white">Your Island Connection</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-white/70">
              <p>Your Island Connection is a locally owned, family-run vacation rental company that’s been helping guests experience the Harpswell Islands since 1982.</p>
              <p>Their team knows Bailey Island, Orr’s Island, Great Island, Cundy’s Harbor and Harpswell because this is home.</p>
              <p>They’ll help you find the cottage. But more importantly, they can help you find the places that make you want to come back.</p>
            </div>
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6">
              <p className="text-sm font-semibold text-white/80">Need something while you’re here?</p>
              <a href={`tel:${HOST.phoneRaw}`} className="mt-2 block font-serif text-2xl text-[#fda36b]">{HOST.phone}</a>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={HOST.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#fda36b] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#132947] transition hover:bg-[#fda36b]/90"
              >
                Visit Your Island Connection <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={`tel:${HOST.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#fda36b] hover:text-[#fda36b]"
              >
                <Phone className="h-4 w-4" /> Call the local crew
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ABOUT BOND ═══ */}
      <section className="bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-2xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">And the coffee people?</p>
            <h2 className="mt-4 font-serif text-3xl leading-tight text-white">We’re Bond Coffee Roasters.</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-white/70">
              <p>Two neighbors who roast small batches of coffee and occasionally find interesting excuses to make things that didn’t exist before.</p>
              <p>For Your Island Connection, that meant making a coffee specifically for mornings on the Maine coast. Because a generic packet of rental-house coffee just didn’t seem right here.</p>
            </div>
            <p className="mt-6 font-serif text-lg italic text-white/50">— Chris + Adam<br />Bond Coffee Roasters</p>
            <div className="mt-8 flex flex-col items-center gap-4">
              <div className="text-center">
                <p className="font-serif text-4xl font-semibold tracking-[0.14em] text-white">BOND</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.32em] text-[#fda36b]">Coffee Roasters</p>
              </div>
              <a
                href={BOND.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#fda36b]/50 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#fda36b] transition hover:bg-[#fda36b] hover:text-[#132947]"
              >
                Meet Bond <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECOND PURCHASE CTA ═══ */}
      <section className="bg-[#132947] py-16">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">Miss the coffee already?</p>
            <p className="mt-4 font-serif text-2xl text-white">{COFFEE.name} — {COFFEE.price} shipped</p>
            <a
              href={COFFEE.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
            >
              Order another bag <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ═══ FINAL SECTION ═══ */}
      <section className="relative overflow-hidden">
        <img
          src={FINAL_IMG}
          alt="Quiet coastal seascape"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0f1f33]/70 via-[#0f1f33]/40 to-[#0f1f33]" />

        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">One more thing.</p>
            <h2 className="mt-6 font-serif text-5xl leading-[0.9] text-white sm:text-6xl">Don’t rush the morning.</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-white/70">The coffee can wait five minutes. So can everything else.</p>
            <div className="mt-12">
              <p className="font-serif text-3xl text-[#fda36b]">{COFFEE.name}</p>
              <p className="mt-2 text-sm text-white/50">Your Island Connection <span className="text-white/30">×</span> Bond Coffee</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/40">{COFFEE.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="bg-[#0a1626] py-12">
        <div className="mx-auto max-w-2xl px-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex items-center gap-6">
              <img src={HOST.logo} alt="Your Island Connection" className="h-10 w-auto opacity-70" />
              <span className="text-white/20">|</span>
              <div className="text-center">
                <p className="font-serif text-lg tracking-[0.14em] text-white/80">BOND</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">Coffee Roasters</p>
              </div>
            </div>
            <p className="text-sm text-white/40">Harpswell, Maine</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              <a href={HOST.localGuideUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 transition hover:text-[#fda36b]">Local Guide</a>
              <a href={HOST.tideChartUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 transition hover:text-[#fda36b]">Tide Chart</a>
              <a href={HOST.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 transition hover:text-[#fda36b]">Your Island Connection</a>
              <a href={BOND.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 transition hover:text-[#fda36b]">Bond Coffee</a>
            </div>
            <Link to="/" className="mt-6 text-xs text-white/30 transition hover:text-[#fda36b]">
              <ArrowUpRight className="mr-1 inline h-3 w-3" />Bond Coffee Roasters
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
